import { CAMERA_PLATFORM_IDS, CAMERA_PLATFORM_VERSION, normalizeCameraPlatform, type CameraPlatformId } from '../../../renderer/src/camera-platform';
export { CAMERA_PLATFORM_IDS, CAMERA_PLATFORM_VERSION, normalizeCameraPlatform, type CameraPlatformId };

/** Metadata for the apparatus carrying the camera. */
export interface CameraPlatformDefinition {
  readonly description: string;
  readonly id: CameraPlatformId;
  readonly label: string;
  readonly version: typeof CAMERA_PLATFORM_VERSION;
}

/** Camera platforms supply automatic behavior; attributes can be authored independently. */
export const CAMERA_PLATFORMS: readonly CameraPlatformDefinition[] = [
  {
    description: "Steady aerial tracking over high-contrast monochrome imagery and 3D buildings.",
    id: "blackbird",
    label: "Blackbird",
    version: CAMERA_PLATFORM_VERSION,
  },
  {
    description: "Locked aerial tracking with a monochrome sensor image and green telemetry.",
    id: "night-hawk",
    label: "Night Hawk",
    version: CAMERA_PLATFORM_VERSION,
  },
  {
    description: "Regional push-in, centered aerial tracking, and a complete-route finish.",
    id: "aerial-follow",
    label: "Night Stalker",
    version: CAMERA_PLATFORM_VERSION,
  },
  {
    description: "A closer view behind the trace marker that turns smoothly with the route.",
    id: "chase",
    label: "Chase",
    version: CAMERA_PLATFORM_VERSION,
  },
  {
    description: "Steady drone tracking with rotation, tilt, altitude, and lens controlled by authored keyframes.",
    id: "orbit",
    label: "DJI Drone",
    version: CAMERA_PLATFORM_VERSION,
  },
  {
    description: "Starts low beside the route, then pulls back, rises, and sweeps across the surrounding terrain.",
    id: "terrain-reveal",
    label: "Little Bird",
    version: CAMERA_PLATFORM_VERSION,
  },
  {
    description: "A fixed full-route composition throughout playback.",
    id: "uav",
    label: "UAV",
    version: CAMERA_PLATFORM_VERSION,
  },
] as const;

/**
 * Tests whether an unknown string names a supported camera platform.
 *
 * @param candidate - Candidate platform identifier.
 * @returns Whether the identifier is present in the stable registry.
 */
export function isCameraPlatformId(candidate: string): candidate is CameraPlatformId {
  return CAMERA_PLATFORM_IDS.some((id) => id === candidate);
}

/**
 * Resolves a camera platform from its stable identifier.
 *
 * @param id - Supported camera platform identifier.
 * @returns Matching immutable camera metadata.
 */
export function resolveCameraPlatform(id: CameraPlatformId): CameraPlatformDefinition {
  return CAMERA_PLATFORMS.find((platform) => platform.id === id)!;
}
